<script setup lang="ts">
  import { TableCell, TableRow } from '@/components/ui/table'
  import { computed, type PropType, ref, nextTick, watch } from 'vue'
  import {
    type CustomerEntityChange as EntityChange,
    type CustomerCaseDataChange,
    type CustomerEntityWithRelationship as EntityWithRelationship,
    type CustomerCaseData,
    type GenericDataMapper,
    FieldTypeEnum,
    ChangeSourceEnum,
    AugmentationVariantEnum
  } from '@/types/generated'
  import { Pencil, FileText, MessageSquare } from 'lucide-vue-next'
  import DatamapperSource from '@/components/app/cases/DatamapperSource.vue'
  import DatamapperValue from '@/components/app/cases/DatamapperValue.vue'
  import DataMapperTimeTravel from '@/components/app/cases/DataMapperTimeTravel.vue'
  import InfoButton from './infoButton.vue'
  import { boolean } from 'yup'
  import { cn } from '@/lib/utils'
  import { yup } from '@/lib/validation'

  const props = defineProps({
    mapperKey: {
      type: String,
      required: true
    },
    isEntity: {
      type: Boolean,
      required: false,
      default: false
    },
    isMandatory: {
      type: Boolean,
      required: false,
      default: false
    },
    customValidator: {
      type: Object as PropType<yup.AnySchema | null>,
      required: false,
      default: () => null
    },
    mappers: {
      type: Object as PropType<GenericDataMapper[]>,
      required: true
    },
    object: {
      type: Object as PropType<EntityWithRelationship | CustomerCaseData>,
      required: true
    },
    allChanges: {
      type: Object as PropType<EntityChange[] | CustomerCaseDataChange[]>,
      required: true
    },
    mode: {
      type: String as PropType<'view' | 'edit'>,
      required: false,
      default: 'view'
    }
  })
  const key = computed<string>(() => {
    // 'entities.generic.first_name' -> 'first_name'
    return props.mapperKey.split('.').pop() as string
  })
  const value = computed<any>(() => {
    if (props.isEntity) {
      return props.object.entity[key.value]
    }
    const [parent, field] = props.mapperKey.split('.')
    return props.object.data[parent][field]
  })

  const changes = computed<EntityChange[] | CustomerCaseDataChange[]>(() => {
    if (props.isEntity) {
      return props.allChanges.filter((change) => change.entities_mapper_key === props.mapperKey)
    }
    return props.allChanges.filter((change) => change.mapper_key === props.mapperKey)
  })

  const latestChange = computed<EntityChange | CustomerCaseDataChange | null>(() => {
    return (
      changes.value
        .slice()
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0] ||
      null
    )
  })
  const mapper = computed<GenericDataMapper | null>(() => {
    return props.mappers.find((mapper) => mapper.key === props.mapperKey) || null
  })
  const label = computed<string>(() => {
    return mapper.value?.label
  })

  const onValueChange = (newValue: any) => {
    emit('onValueChange', key.value, props.mapperKey, newValue)
  }
  const handleValueChangeError = (mapperKey: string, isError: boolean) => {
    emit('valueChangeError', mapperKey, isError)
  }
  const emit = defineEmits(['onValueChange', 'valueChangeError'])
  const getSourceStyle = (source?: string) => {
    if (source?.includes('document')) return 'bg-[#7C3AED] text-white'
    if (source?.includes('chat')) return 'bg-[#7C3AED] text-white'
    return 'bg-gray-100'
  }

  // Update the value when a version is selected from TimeTravel component
  const handleVersionSelect = async (newValue: any) => {
    // Create a new change with the selected value
    await onValueChange(newValue)
  }

  const showTimeTravel = computed<boolean>(() => {
    const displayTimeTravelOnFields: FieldTypeEnum[] = [
      FieldTypeEnum.MULTISELECT,
      FieldTypeEnum.SELECT,
      FieldTypeEnum.TIMEFIELD,
      FieldTypeEnum.DATEFIELD,
      FieldTypeEnum.TEXTINPUT,
      FieldTypeEnum.NUMBERINPUT,
      FieldTypeEnum.DECIMALNUMBERINPUT,
      FieldTypeEnum.EMAILINPUT,
      FieldTypeEnum.PHONEINPUT,
      FieldTypeEnum.URLINPUT,
      FieldTypeEnum.YESNO
    ]
    return mapper.value
      ? displayTimeTravelOnFields.includes(mapper.value.field_type) && changes.value.length > 1
      : false
  })

  const showRow = computed<boolean>(() => {
    const showableRows = [
      FieldTypeEnum.MULTISELECT,
      FieldTypeEnum.SELECT,
      FieldTypeEnum.TIMEFIELD,
      FieldTypeEnum.DATEFIELD,
      FieldTypeEnum.TEXTINPUT,
      FieldTypeEnum.TEXTAREA,
      FieldTypeEnum.NUMBERINPUT,
      FieldTypeEnum.DECIMALNUMBERINPUT,
      FieldTypeEnum.EMAILINPUT,
      FieldTypeEnum.PHONEINPUT,
      FieldTypeEnum.URLINPUT,
      FieldTypeEnum.YESNO
    ]
    return mapper.value ? showableRows.includes(mapper.value.field_type) : false
  })

  const disabled = computed(() => {
    return props.mode === 'view'
  })

  const disabledColor = computed(() => {
    return props.mode === 'view' ? 'text-gray-400' : ''
  })
</script>
<template>
  <TableRow
    v-if="mapper && showRow"
    class="hover:bg-muted/50"
    :data-cy="`dataTableRow-${mapper.key.replace(/\./g, '-')}`"
  >
    <TableCell
      v-if="!isEntity"
      class="w-[10%] truncate p-3"
      data-cy="lawField"
    >
      {{ mapper.law_field }}
    </TableCell>
    <TableCell
      :class="[isEntity ? 'w-[15%]' : 'w-[30%]', disabledColor, 'truncate p-3 font-medium']"
      data-cy="fieldLabel"
    >
      <div class="inline">
        {{ label }}
      </div>
      <div
        v-if="isMandatory"
        class="inline pr-1 text-red-600"
      >
        *
      </div>
    </TableCell>
    <TableCell
      :class="[isEntity ? 'w-[30%]' : 'w-[25%]', disabledColor, 'overflow-hidden p-3']"
      data-cy="fieldValue"
    >
      <DatamapperValue
        :key="`${props.object.uuid}-${props.mapperKey}`"
        :value="value"
        :mapper="mapper"
        :disabled="disabled"
        :custom-validator="customValidator"
        @on-value-change="onValueChange"
        @value-change-error="handleValueChangeError"
      />
    </TableCell>
    <TableCell
      :class="[isEntity ? 'w-[30%]' : 'w-[25%]', disabledColor, 'overflow-hidden p-3']"
      data-cy="sourceColumn"
    >
      <div class="flex w-full items-center justify-between">
        <DatamapperSource :change="latestChange" />
        <InfoButton
          v-if="latestChange?.ai_augmentation?.explanation"
          :id="latestChange.ai_augmentation.uuid"
          :explanation="latestChange.ai_augmentation.explanation"
        />
      </div>
    </TableCell>
    <TableCell
      :class="[isEntity ? 'w-[15%]' : 'w-[10%]', 'p-3']"
      data-cy="timestampColumn"
    >
      <DataMapperTimeTravel
        :changes="changes"
        :latest-change="latestChange"
        :show-time-travel="showTimeTravel"
        @on-version-select="handleVersionSelect"
      />
    </TableCell>
  </TableRow>
</template>
